import { UpdateAdditionalDetailsSchema } from '@buyerflo/core/schemas/purchaser'
import { InternalPurchaserValueConfigSchema, ShowInTabOption } from '@buyerflo/core/schemas/purchaserConfig'
import { zodResolver } from '@hookform/resolvers/zod'
import Button from 'components/button'
import { Label, Text, View, YStack } from 'tamagui'
import { useMutation } from '@tanstack/react-query'
import { usePurchaserConfigApi } from 'apiProviders'
import Loading from 'components/Loading'
import Select from 'components/select'
import { useShowSnack } from 'components/snackbar'
import { useEffect } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useTRPC } from 'utils/trpc'
import { usePurchaserContext } from './purchaserProvider'

export const RenderPurchaserValue = ({
    valueConfig,
    disabled,
}: {
    valueConfig: InternalPurchaserValueConfigSchema
    disabled?: boolean
}) => {
    const { control, getValues } = useFormContext()

    return (
        <Controller
            name={`additionalDetails.${valueConfig.id}`}
            control={control}
            render={({ field }) => (
                <YStack flex={1}>
                    <Label>{valueConfig.configName}</Label>
                    <Select
                        {...field}
                        disabled={disabled}
                        onChange={(e, val) => field.onChange(val)}
                        renderValue={x => {
                            const val = typeof x === 'string' ? x : x?.value

                            return (
                                x && (
                                    <View
                                        backgroundColor={
                                            (valueConfig.configValues.find(v => v.value === val)?.color as any) ? `$${valueConfig.configValues.find(v => v.value === val)?.color}3` : '$gray3'
                                        }
                                        paddingHorizontal="$2"
                                        paddingVertical="$1"
                                        borderRadius={999}
                                    >
                                        <Text fontSize="$2">{val}</Text>
                                    </View>
                                )
                            )
                        }}
                        multiple={false}
                    >
                        {valueConfig.configValues.map(({ value }) => (
                            <option
                                key={value}
                                value={value}
                            >
                                {value}
                            </option>
                        ))}
                    </Select>
                </YStack>
            )}
        />
    )
}

export const RenderPurchaserValues = ({ tab, disabled }: { tab: ShowInTabOption; disabled?: boolean }) => {
    const purchaserConfigApi = usePurchaserConfigApi(tab)

    return (
        <YStack
            gap="$4"
            maxWidth={500}
        >
            {purchaserConfigApi.data?.map(config => (
                <RenderPurchaserValue
                    key={config.id}
                    valueConfig={config}
                    disabled={disabled}
                />
            ))}
        </YStack>
    )
}

const AdditionalValues = () => {
    const purchaserConfigApi = usePurchaserConfigApi('purchaser')
    const { purchaser, setPurchaser } = usePurchaserContext()
    const trpc = useTRPC()
    const showSnack = useShowSnack()
    const updateAdditionalDetailsMutation = useMutation(
        trpc.purchaser.updateAdditionalDetails.mutationOptions(),
    )

    const hookForm = useForm<UpdateAdditionalDetailsSchema>({
        defaultValues: {
            additionalDetails: purchaser.additionalDetails ?? {},
        },
        resolver: zodResolver(UpdateAdditionalDetailsSchema),
    })

    const { control, handleSubmit, formState, getValues, setValue, setError, trigger, reset, watch } =
        hookForm

    useEffect(() => {
        console.log('resetting', purchaser.additionalDetails ?? {})
        reset({ additionalDetails: purchaser.additionalDetails ?? {} })
    }, [purchaser.purchaserId])

    if (Object.keys(formState.errors).length > 0) {
        console.log(formState.errors)
    }

    const onSubmit = async data => {
        console.log('submitting', data)

        const updatedData = Object.entries(data)
            .filter(([key, value]) => {
                console.log('filter', purchaser.additionalDetails[key], value)
                return purchaser.additionalDetails[key] !== value
            })
            .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

        console.log('updatedData', updatedData)

        if (Object.keys(updatedData).length === 0) {
            return
        }

        try {
            const updatedPurchaser = await updateAdditionalDetailsMutation.mutateAsync({
                purchaserId: purchaser.purchaserId,
                additionalDetails: updatedData,
            })
            if (updatedPurchaser) {
                setPurchaser(updatedPurchaser)
            }
            showSnack('Purchaser updated', 'success')
        } catch (e) {
            console.error(e)
            showSnack('Error updating purchaser', 'danger')
        }
    }

    if (!purchaserConfigApi.data) {
        return <Loading />
    }

    return (
        <FormProvider {...hookForm}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <RenderPurchaserValues tab="purchaser" />
                <View>
                    <Button
                        marginTop="$4"
                        theme="primary"
                        onPress={handleSubmit(onSubmit)}
                    >
                        Save
                    </Button>
                </View>
            </form>
        </FormProvider>
    )
}

export default AdditionalValues
